import { useState, useMemo } from 'react';

const TRANSACTION_FEE = 0.20;
const RECRA_FEE_PER_TRANSACTIE = 0.10;
const EXPLOITATIE_KOSTEN_PER_MINUUT = 0.06;
const MIN_CONTRACT_MAANDEN = 36;
const LEASE_PRIJS_PER_TERMINAL = 39;
const KOOPPRIJS_PER_TERMINAL = 699;
const MUURBEVESTIGING_PER_TERMINAL = 59;
const GARANTIE_PRIJS_PER_TERMINAL = 3;
const MRG_MIN_TRANSACTIES = 500;

export function useLeaseCalculator(initialValues = {}) {
    const [inputs, setInputs] = useState({
        aantalDouches: initialValues.aantalDouches ?? 12,
        douchebeurtenWaarde: initialValues.douchebeurtenWaarde ?? 20,
        douchebeurtenPerDag: initialValues.douchebeurtenPerDag ?? true,
        prijsPerMinuut: initialValues.prijsPerMinuut ?? 0.75,
        gemiddeldeDouchetijd: initialValues.gemiddeldeDouchetijd ?? 7,
        maandenOpen: initialValues.maandenOpen ?? 7,
        leasePrijsPerTerminal: LEASE_PRIJS_PER_TERMINAL,
        bezettingsgraad: initialValues.bezettingsgraad ?? 65,
        garantie: initialValues.garantie ?? false,
        muurbevestiging: initialValues.muurbevestiging ?? true
    });

    const updateInput = (key, value) => {
        setInputs(prev => ({ ...prev, [key]: value }));
    };

    const toggleDouchebeurtenMode = () => {
        setInputs(prev => {
            const isCurrentlyPerDag = prev.douchebeurtenPerDag;
            let newWaarde;
            if (isCurrentlyPerDag) {
                newWaarde = prev.douchebeurtenWaarde * 30;
            } else {
                newWaarde = Math.round(prev.douchebeurtenWaarde / 30);
            }
            return {
                ...prev,
                douchebeurtenPerDag: !isCurrentlyPerDag,
                douchebeurtenWaarde: newWaarde
            };
        });
    };

    const results = useMemo(() => {
        const { 
            aantalDouches, 
            douchebeurtenWaarde, 
            douchebeurtenPerDag, 
            prijsPerMinuut, 
            gemiddeldeDouchetijd,
            maandenOpen,
            leasePrijsPerTerminal,
            bezettingsgraad,
            garantie
        } = inputs;
        
        // Raw monthly showers (what user enters)
        const douchebeurtenPerMaandRaw = douchebeurtenPerDag 
            ? douchebeurtenWaarde * 30 
            : douchebeurtenWaarde;
        
        // REALISTISCHE CORRECTIE: apply bezettingsgraad
        const correctieFactor = bezettingsgraad / 100;
        const douchebeurtenPerMaand = Math.round(douchebeurtenPerMaandRaw * correctieFactor);
        
        // Total minutes per month (corrected)
        const totaalMinutenPerMaand = douchebeurtenPerMaand * gemiddeldeDouchetijd;
        
        // Transactions = corrected number of showers
        const actueleTransacties = douchebeurtenPerMaand;
        
        // INKOMSTEN (based on corrected numbers)
        const brutoOmzet = totaalMinutenPerMaand * prijsPerMinuut;
        
        // TRANSACTIEKOSTEN (€0.20 per transaction, auto-deducted)
        const transactiekosten = actueleTransacties * TRANSACTION_FEE;
        const nettoOmzet = brutoOmzet - transactiekosten;
        
        // EXPLOITATIEKOSTEN (gas/water/elektra)
        const exploitatieKosten = totaalMinutenPerMaand * EXPLOITATIE_KOSTEN_PER_MINUUT;
        
        // LEASE KOSTEN (vast per terminal)
        const leaseKostenPerMaand = leasePrijsPerTerminal * aantalDouches;
        
        // GARANTIE (optionele add-on)
        const garantieKosten = garantie ? GARANTIE_PRIJS_PER_TERMINAL * aantalDouches : 0;
        
        // MRG - MINIMUM REVENUE GUARANTEE ("Inbegrepen gebruik")
        const mrgMinTransacties = MRG_MIN_TRANSACTIES;
        const mrgTekort = Math.max(0, mrgMinTransacties - actueleTransacties);
        const mrgBijbetaling = mrgTekort * RECRA_FEE_PER_TRANSACTIE;
        const mrgActief = mrgTekort > 0;
        
        // TOTALE KOSTEN (inclusief MRG bijbetaling en garantie als van toepassing)
        const totaleKosten = exploitatieKosten + leaseKostenPerMaand + garantieKosten + mrgBijbetaling;
        
        // NETTO WINST (LEASE)
        const nettoWinstMaand = nettoOmzet - totaleKosten;
        const nettoWinstJaar = nettoWinstMaand * maandenOpen;

        // KOOP scenario (eenmalige investering, geen maandelijkse lease)
        const koopprijsTerminal = KOOPPRIJS_PER_TERMINAL * aantalDouches;
        const muurbevestigingKosten = inputs.muurbevestiging ? MUURBEVESTIGING_PER_TERMINAL * aantalDouches : 0;
        const koopInvestering = koopprijsTerminal + muurbevestigingKosten;
        const totaleKostenKoop = exploitatieKosten + garantieKosten + mrgBijbetaling;
        const nettoWinstMaandKoop = nettoOmzet - totaleKostenKoop;
        const nettoWinstJaarKoop = nettoWinstMaandKoop * maandenOpen;
        const terugverdientijdMaandenKoop = nettoWinstMaandKoop > 0
            ? Math.ceil(koopInvestering / nettoWinstMaandKoop)
            : 0;
        
        // Break-even prijs per minuut (alle kosten inclusief transactie + MRG)
        const alleKostenPerMaand = transactiekosten + totaleKosten;
        const breakevenPrijsPerMinuut = totaalMinutenPerMaand > 0 
            ? alleKostenPerMaand / totaalMinutenPerMaand 
            : 0;
        
        // Per douche inzichten
        const opbrengstPerDouche = douchebeurtenPerMaand > 0 
            ? nettoOmzet / douchebeurtenPerMaand : 0;
        const kostenPerDouche = douchebeurtenPerMaand > 0 
            ? totaleKosten / douchebeurtenPerMaand : 0;
        
        return {
            // Input echo
            aantalDouches,
            leasePrijsPerTerminal,
            douchebeurtenPerDag,
            douchebeurtenWaarde,
            gemiddeldeDouchetijd,
            prijsPerMinuut,
            minContractMaanden: MIN_CONTRACT_MAANDEN,
            maandenOpen,
            bezettingsgraad,
            garantie,
            
            // Correctie
            douchebeurtenPerMaandRaw,
            correctieFactor,
            douchebeurtenPerMaand,
            douchebeurtenPerLezerPerDag: douchebeurtenPerDag 
                ? Math.round((douchebeurtenWaarde * correctieFactor / aantalDouches) * 10) / 10
                : Math.round((douchebeurtenWaarde * correctieFactor / 30 / aantalDouches) * 10) / 10,
            totaalMinutenPerMaand,
            actueleTransacties,
            
            // Inkomsten
            brutoOmzet,
            transactiekosten,
            nettoOmzet,
            opbrengstPerDouche,
            
            // Kosten breakdown
            exploitatieKosten,
            leaseKostenPerMaand,
            garantieKosten,
            
            // MRG
            mrgMinTransacties,
            mrgTekort,
            mrgBijbetaling,
            mrgActief,
            
            totaleKosten,
            kostenPerDouche,
            
            // Resultaat (LEASE)
            nettoWinstMaand,
            nettoWinstJaar,
            breakevenPrijsPerMinuut,
            isProfitable: nettoWinstMaand > 0,

            // Resultaat (KOOP)
            koopprijsPerTerminal: KOOPPRIJS_PER_TERMINAL,
            muurbevestigingPerTerminal: MUURBEVESTIGING_PER_TERMINAL,
            koopprijsTerminal,
            muurbevestigingKosten,
            koopInvestering,
            nettoWinstMaandKoop,
            nettoWinstJaarKoop,
            terugverdientijdMaandenKoop,
            isProfitableKoop: nettoWinstMaandKoop > 0
        };
    }, [inputs]);

    return {
        inputs,
        updateInput,
        toggleDouchebeurtenMode,
        results
    };
}
